import * as React from 'react';
import {KitchenSink} from './KitchenSink';
import {iconName, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {
  ElementPreset,
  Plugins,
  type Type,
  withMeta,
  withSystem,
  Component,
  TypeImplementation,
  Expressions,
  ExpressionPath,
} from '@packages/systems/wfdl';
import {
  ExpressionEditor,
  extendExpressionEditorActionAtPath,
} from '@packages/systems/ExpressionEditor-utils';
import {Color} from './ColorTypeAlias';
import {GPS} from './GPSTypeAlias';
import {AddChildren} from './AddChildrenAlias';
import {TBoolean as TUnionBoolean} from '@packages/systems/plugins-utils/Boolean';

import KitchenSinkPreset from './icons/kitchen-sink-preset';
import KitchenSinkElement from './icons/kitchen-sink-element';
import LabPreset from './icons/lab-icon-preset';
import LabElement from './icons/lab-icon-element';

import {
  ECall,
  functionOf,
  TBoolean,
  TNumber,
  TText,
  TList,
  TRecord,
  TEmptyRecord,
  TUnion,
  TEnum,
  enumOf,
  EElement,
  EText,
  ERecord,
  EBoolean,
  ENumber,
  EEnum,
  EUnion,
  EList,
  TGeneric,
  TTypeReference,
  TTypeApplication,
  TTypeConstructor,
  TListOfElement,
  EDistinct,
} from '@packages/systems/wfdl-ast';

import {ContextualExpressions} from '@packages/utilities/contextual-expression';

const {getIn} = ContextualExpressions;

const privilegedLabPlugin = Plugins.name('PrivilegedLab');
const kitchenSinkPresetIcon = iconName('KitchenSinkPreset');
const icons = {
  KitchenSinkPreset,
  KitchenSinkElement,
  LabPreset,
  LabElement,
} as const;

const paramPath = [{in: 'Record', at: 'param'}] satisfies ExpressionPath;
const TStrMap = (arg: Type): Type =>
  TTypeApplication({
    con: TTypeConstructor(
      qualify(Plugins.name('PrivilegedLab'), typeName('StrMap'))
    ),
    arg,
  });
export const plugin = Plugins.PrivilegedPlugin({
  icons,
  atoms: {KitchenSink},
  dataTypes: {
    AddChildren,
    StrMap: TypeImplementation.Custom({
      params: ['Value'],
      render: () => null,
    }),
    URL: TypeImplementation.Alias({
      type: TText,
      render: ({expr, dispatch}) => {
        return <ExpressionEditor expression={expr} dispatch={dispatch} />;
      },
    }),
    Color,
    GPS,
    ParameterizedAlias: TypeImplementation.Alias({
      params: ['T'],
      type: TTypeReference('T'),
      render: ({expr, dispatch}) => {
        return <ExpressionEditor expression={expr} dispatch={dispatch} />;
      },
    }),
    ParameterizedRecordField: TypeImplementation.Alias({
      params: ['T'],
      type: TRecord({
        const: TText,
        param: TTypeReference('T'),
      }),
      render: ({expr, dispatch}) => {
        const paramExpr = getIn(expr, paramPath);
        if (!paramExpr) {
          // Malformed expr, lab only feature no need to reportBug or the sorts just fail out
          return null;
        }
        return (
          <ExpressionEditor
            expression={paramExpr}
            dispatch={extendExpressionEditorActionAtPath(dispatch)(paramPath)}
          />
        );
      },
    }),
    CoolDistinct: TypeImplementation.Unique({
      type: TText,
      render: ({expr, dispatch}) => {
        return <ExpressionEditor expression={expr} dispatch={dispatch} />;
      },
    }),
  },
  values: {
    textToListOfElements: {
      envType: {
        dataType: withSystem(
          {label: 'Text to Element'},
          functionOf(
            withSystem(
              {defaultValue: true, label: 'Uppercase?'},
              TTypeConstructor(
                qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
              )
            ),
            functionOf(withSystem({isBindable: true}, TText), TListOfElement)
          )
        ),
        exported: 'hidden',
        implicit: 'prefilledArgs',
      },
      // @ts-expect-error - TS7006 - Parameter 'uppercase' implicitly has an 'any' type.
      value: (uppercase) => (x: any) => [
        {
          text: true,
          type: null,
          id: 'temp',
          children: [],
          data: {value: uppercase ? x.toUpperCase() : x},
        },
      ],
    },
    // This value should never display in the binding menu
    nonExportedPluginValue: {
      envType: {
        dataType: withSystem(
          {label: 'nonExportedPluginValue', isBindable: true},
          TText
        ),
      },
      value: 'nonExportedPluginValue',
    },
    aliasParamNumber: {
      envType: {
        dataType: TTypeApplication({
          con: TTypeConstructor(
            qualify(
              Plugins.name('PrivilegedLab'),
              typeName('ParameterizedAlias')
            )
          ),
          arg: TNumber,
        }),
        exported: 'visible',
      },
      value: 12,
    },
    favColor: {
      envType: {
        dataType: TRecord({r: TNumber, g: TNumber, b: TNumber}),
        exported: 'visible',
      },
      value: {r: 40, g: 40, b: 40},
    },
    user: {
      envType: {
        dataType: TRecord({
          'first name': TText,
          'last name': TText,
          'favorite ice cream': TText,
          'worst song of all time': TText,
          age: TNumber,
        }),
        exported: 'visible',
      },
      value: {
        'first name': 'dustan',
        'last name': 'kasten',
        'favorite ice cream': 'licorice',
        'worst song of all time': 'yes',
        age: 30,
      },
    },
    villain: {
      envType: {
        dataType: withSystem({label: 'Villain', isBindable: true}, TText),
        exported: 'visible',
      },
      value: 'Joker',
    },
    hero: {
      envType: {
        dataType: withSystem({label: 'Hero', isBindable: true}, TText),
        exported: 'visible',
      },
      value: 'Batman',
    },
    // This is mocking a frontloaded call to `isLoggedInMock`
    // with the user logged in set to true
    nestedIsLoggedIn: {
      envType: {
        dataType: withSystem(
          {label: 'Logged in user', isBindable: true},
          functionOf(
            withMeta(
              {variant: 'menu'},
              TEnum({
                eq: 'Is logged in',
                ne: 'Is not logged in',
              })
            ),
            TBoolean
          )
        ),
        exported: 'visible',
      },
      expression: ECall({
        fun: Expressions.qualifiedVariable('PrivilegedLab', 'isLoggedInMock'),
        arg: EBoolean(true),
      }),
    },
    isLoggedInMock: {
      envType: {
        dataType: withSystem(
          {label: 'Logged in user', isBindable: true},
          functionOf(
            TBoolean,
            functionOf(
              withMeta(
                {variant: 'menu'},
                TEnum({
                  eq: 'Is logged in',
                  ne: 'Is not logged in',
                })
              ),
              TBoolean
            )
          )
        ),
        exported: 'no',
      },
      // @ts-expect-error - TS7006 - Parameter 'userLoggedInMockedStatus' implicitly has an 'any' type.
      value: (userLoggedInMockedStatus) => (selectedEquality: string) => {
        if (selectedEquality === 'eq') {
          return userLoggedInMockedStatus === true;
        } else {
          return userLoggedInMockedStatus === false;
        }
      },
    },
    // This is mocking a frontloaded call to `usysELGMock`
    // (hasAccessGroup) with an access group of "premium"
    nestedUsysELGMock: {
      envType: {
        dataType: withSystem(
          {label: 'Access group', isBindable: true},
          functionOf(
            withMeta(
              {variant: 'menu'},
              TEnum({
                eq: 'Equals',
                ne: 'Does not equal',
              })
            ),
            functionOf(
              withMeta(
                {variant: 'menu'},
                TEnum({
                  trial: 'Trial',
                  base: 'Base',
                  premium: 'Premium',
                  vip: 'VIP',
                })
              ),
              TBoolean
            )
          )
        ),
        exported: 'visible',
      },
      expression: ECall({
        fun: Expressions.qualifiedVariable('PrivilegedLab', 'usysELGMock'),
        arg: EText('premium'),
      }),
    },
    usysELGMock: {
      envType: {
        dataType: functionOf(
          TText,
          functionOf(
            withMeta(
              {variant: 'menu'},
              TEnum({
                eq: 'Equals',
                ne: 'Does not equal',
              })
            ),
            functionOf(
              withMeta(
                {variant: 'menu'},
                TEnum({
                  trial: 'Trial',
                  base: 'Base',
                  premium: 'Premium',
                  vip: 'VIP',
                })
              ),
              TBoolean
            )
          )
        ),
        exported: 'no',
      },
      value:
        // @ts-expect-error - TS7006 - Parameter 'accessGroupOfMockedUser' implicitly has an 'any' type.

        (accessGroupOfMockedUser) =>
          (selectedEquality: string) =>
          (selectedAccessGroup: any) => {
            if (selectedEquality === 'eq') {
              return selectedAccessGroup === accessGroupOfMockedUser;
            } else {
              return selectedAccessGroup !== accessGroupOfMockedUser;
            }
          },
    },
    bestNr: {
      envType: {
        dataType: withSystem({label: 'My fav numb', isBindable: true}, TNumber),
        exported: 'visible',
      },
      value: 9,
    },
    bestSeason: {
      envType: {
        dataType: withSystem(
          {label: 'Best Season'},
          TEnum({
            Winter: 'Winter',
            Spring: 'Spring',
            Summer: 'Summer',
            Fall: 'Fall',
          })
        ),
        exported: 'visible',
      },
      value: 'Summer',
    },
    bestBoolean: {
      envType: {
        dataType: withSystem({label: 'Best Boolean'}, TUnionBoolean),
        exported: 'visible',
      },
      expression: EUnion({
        tag: 'True',
        value: ERecord({}),
      }),
    },
    bestListOfElements: {
      envType: {
        dataType: withSystem({label: 'Best List of Elements'}, TListOfElement),
        exported: 'visible',
      },
      value: [
        {
          id: 'best-list-el-id',
          type: 'String',
          pluginType: 'Basic',
          data: {value: 'best list of elements'},
          children: [],
          text: true,
        },
      ],
    },
    toUpperCase: {
      envType: {
        dataType: withSystem(
          {label: 'To UpperCase'},
          functionOf(
            withSystem({label: 'Text', isBindable: true}, TText),
            withSystem({label: 'Uppercased text'}, TText)
          )
        ),
        exported: 'visible',
      },
      // @ts-expect-error - TS7006 - Parameter 'str' implicitly has an 'any' type.
      value: (str) => str.toUpperCase(),
    },
    goodPeople: {
      envType: {
        dataType: TList(
          TRecord({
            alias: TText,
            age: withMeta({value: {min: 0, max: 150, step: 1}}, TNumber),
          })
        ),
        exported: 'visible',
      },
      value: [
        {alias: 'Fred', age: 24},
        {alias: 'Daphne', age: 26},
        {alias: 'Velma', age: 31},
        {alias: 'Shaggy', age: 27},
        {alias: 'Scooby', age: 15},
      ],
    },
    darkStyles: {
      envType: {
        dataType: TList(
          TRecord({
            styleProperty: TText,
            value: TText,
          })
        ),
        exported: 'visible',
      },
      value: [
        {styleProperty: 'backgroundColor', value: 'black'},
        {styleProperty: 'color', value: 'gray'},
      ],
    },
    enumToText: {
      envType: {
        dataType: withSystem(
          {label: 'To Text'},
          TGeneric({
            arg: 'EnumValues',
            body: functionOf(enumOf(TTypeReference('EnumValues')), TText),
          })
        ),
        exported: 'visible',
      },
      value: String,
    },
    numberToText: {
      envType: {
        dataType: withSystem(
          {label: 'To Text'},
          functionOf(
            withSystem({label: 'Number', isBindable: true}, TNumber),
            withSystem({label: 'Text'}, TText)
          )
        ),
        implicit: 'prefilledArgs',
        exported: 'hidden',
      },
      value: String,
    },
    booleanToUnion: {
      envType: {
        dataType: withSystem(
          {label: 'To Union'},
          functionOf(
            TBoolean,
            TUnion({
              true: TEmptyRecord,
              false: TEmptyRecord,
            })
          )
        ),
        exported: 'visible',
      },
      // @ts-expect-error - TS7006 - Parameter 'branch' implicitly has an 'any' type.
      value: (branch) => {
        return {
          tag: branch === true ? 'true' : 'false',
          value: {},
        };
      },
    },
    length: {
      envType: {
        dataType: withSystem(
          {label: 'Number of Items'},
          TGeneric({
            arg: 'ListElementType',
            body: functionOf(TList(TTypeReference('ListElementType')), TNumber),
          })
        ),
        exported: 'visible',
      },
      // @ts-expect-error - TS7006 - Parameter 'list' implicitly has an 'any' type.
      value: (list) => list.length,
    },
    map: {
      envType: {
        dataType: withSystem(
          {label: 'Map'},
          TGeneric({
            arg: 'ListElementType',
            body: TGeneric({
              arg: 'ResultListElementType',
              body: functionOf(
                functionOf(
                  TRecord({
                    item: TTypeReference('ListElementType'),
                    index: TNumber,
                  }),
                  TTypeReference('ResultListElementType')
                ),
                functionOf(
                  TList(TTypeReference('ListElementType')),
                  TList(TTypeReference('ResultListElementType'))
                )
              ),
            }),
          })
        ),
        exported: 'hidden',
      },
      // @ts-expect-error - TS7006 - Parameter 'mapper' implicitly has an 'any' type.
      value: (mapper) => (list: any) =>
        // @ts-expect-error - TS7006 - Parameter 'item' implicitly has an 'any' type. | TS7006 - Parameter 'index' implicitly has an 'any' type.
        list.map((item, index) =>
          mapper({
            item,
            index,
          })
        ),
    },
    toStyle: {
      envType: {
        dataType: withSystem(
          {
            label: 'Styles',
          },
          functionOf(
            TList(
              TRecord({
                styleProperty: withSystem(
                  {
                    label: 'Property',
                  },
                  TText
                ),
                value: withSystem(
                  {
                    label: 'Value',
                  },
                  TText
                ),
              })
            ),
            TStrMap(TText)
          )
        ),
      },
      // @ts-expect-error - TS7006 - Parameter 'styles' implicitly has an 'any' type.
      value: (styles) =>
        // @ts-expect-error - TS2347 - Untyped function calls may not accept type arguments. | TS7006 - Parameter 'memo' implicitly has an 'any' type. | TS7006 - Parameter 'style' implicitly has an 'any' type.
        styles.reduce<Record<string, any>>((memo, style) => {
          memo[style.styleProperty] = style.value;
          return memo;
        }, {}),
    },
    concat: {
      envType: {
        dataType: withSystem(
          {label: 'Concat'},
          TGeneric({
            arg: 'ListElementType',
            body: functionOf(
              TList(TTypeReference('ListElementType')),
              functionOf(
                TList(TTypeReference('ListElementType')),
                TList(TTypeReference('ListElementType'))
              )
            ),
          })
        ),
      },
      // @ts-expect-error - TS7006 - Parameter 'a1' implicitly has an 'any' type.
      value: (a1) => (a2: any) => a1.concat(a2),
    },
    italic: {
      envType: {
        dataType: withSystem({label: 'Italic'}, TText),
        exported: 'visible',
      },
      expression: EText('italic'),
    },
  },
  elementPresets: {
    KitchenSink: ElementPreset.create({
      factory: EElement({
        id: 'Kitchen Sink',
        type: qualify(
          Plugins.name('PrivilegedLab'),
          Component.name('KitchenSink')
        ),
        data: ERecord({
          param: ENumber(431),
          paramRec: ERecord({
            const: EText('this'),
            param: ENumber(134),
          }),
          interp: EText('test value'),
          slider: ENumber(10),
          triStateBooleanField: EEnum('false'),
          manylines: EText('asdasd'),
          contact: EUnion({
            tag: 'Mail',
            value: EText('1007 Mountain Drive, Gotham'),
          }),
          styles: EList([
            ERecord({
              styleProperty: EText('backgroundColor'),
              value: EText('red'),
            }),
            ERecord({
              styleProperty: EText('padding'),
              value: EText('20px'),
            }),
            ERecord({
              styleProperty: EText('color'),
              value: EText('white'),
            }),
            ERecord({
              styleProperty: EText('fontSize'),
              value: EText('32px'),
            }),
            ERecord({
              styleProperty: EText('lineHeight'),
              value: EText('1.2'),
            }),
          ]),
          bestSeason: EEnum('Spring'),
          favoriteColor: ERecord({
            r: ENumber(10),
            g: ENumber(10),
            b: ENumber(10),
          }),
          gps: ERecord({
            gpslatlong: ERecord({
              lat: ENumber(40.74),
              long: ENumber(-73.98),
            }),
          }),
          anyList: EList([]),
          colorList: EList([]),
          url: EText('asdasd'),
          candidates: EList([]),
          isCool: EBoolean(true),
          isCoolest: EUnion({
            tag: 'True',
            value: ERecord({}),
          }),
          person: ERecord({
            alias: EText('Bruce Wayne'),
            age: ENumber(33),
          }),
          coolDistinct: EDistinct({
            name: qualify(
              Plugins.name('PrivilegedLab'),
              typeName('CoolDistinct')
            ),
            value: EText('Distinctly cool text'),
          }),
          children: EList([
            EElement({
              id: 'KitchenSink h1',
              type: qualify(Plugins.name('Basic'), Component.name('Heading')),
              data: ERecord({
                tag: EEnum('h3'),
                children: EList([
                  EElement({
                    id: 'KitchenSink string',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('String')
                    ),
                    data: EText('ElementPreset provided child!'),
                  }),
                ]),
              }),
            }),
          ]),
        }),
      }),
      label: 'Kitchen Sink',
      icon: {
        medium: qualify(privilegedLabPlugin, kitchenSinkPresetIcon),
        small: qualify(privilegedLabPlugin, kitchenSinkPresetIcon),
      },
      category: 'Lab',
      helpData: {
        description: [
          'The Kitchen Sink is the Core team\'s Privileged Lab Atom of a 3rd party plugin. The implementation is using "privileged" APIs and would not be safe to expose to third parties.',
        ],
        tip: 'The Kitchen Sink has a Plugin Component counterpart called Kitchen Sink Component',
      },
    }),
  },
});
